import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Amplify } from 'aws-amplify';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import './index.css';
import { validateAppRedirectUri } from './services/auth-redirect';

// React Query クライアント設定
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5分
      retry: 1,
    },
  },
});

// URLパラメータからCognito設定を取得
const params = new URLSearchParams(window.location.search);
const clientId = params.get('client_id') ?? import.meta.env.VITE_COGNITO_CLIENT_ID ?? '';
// redirect_uri は許可リストで検証する（外部 URL へのトークン漏洩対策）
const redirectUri = validateAppRedirectUri(
  params.get('redirect_uri') ?? import.meta.env.VITE_REDIRECT_URI,
);

// Amplify設定
const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN ?? '';
const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID ?? '';

if (!userPoolId || !clientId || !cognitoDomain) {
  console.warn('[Auth] Cognito設定が不完全です。認証機能が無効化されています。', {
    userPoolId: !!userPoolId,
    clientId: !!clientId,
    cognitoDomain: !!cognitoDomain,
  });
}

if (userPoolId && clientId && cognitoDomain) {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId: clientId,
        loginWith: {
          oauth: {
            domain: cognitoDomain.replace('https://', ''),
            scopes: ['openid', 'email', 'profile'],
            redirectSignIn: [redirectUri],
            redirectSignOut: [redirectUri.replace('/callback', '/logout')],
            responseType: 'code',
          },
        },
      },
    },
  });
}

// PKCE パラメータをセッションに保存
const state = params.get('state');
const codeChallenge = params.get('code_challenge');
if (state) sessionStorage.setItem('auth_state', state);
if (codeChallenge) sessionStorage.setItem('auth_code_challenge', codeChallenge);
if (redirectUri) sessionStorage.setItem('auth_redirect_uri', redirectUri);
if (clientId) sessionStorage.setItem('auth_client_id', clientId);

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
